import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { EventSourceParserStream } from 'eventsource-parser/stream';
import { axios, notify } from '@bento-dental/core';
import { Messages, MessagesContextResponse } from './types';
import { Message } from './types';

export async function* parseSSEStream(stream: ReadableStream) {
  const sseReader = stream
    .pipeThrough(new TextDecoderStream())
    .pipeThrough(new EventSourceParserStream())
    .getReader();

  while (true) {
    const { done, value } = await sseReader.read();
    if (done) break;
    yield value.data;
  }
}

export const updateChatTitle = (sessionId: string, messages: Message[]) => {
  if (messages.length <= 2) {
    const userMessage = messages.find((msg) => msg.role === 'user')?.content || '';
    const title =
      userMessage.length > 20 ? userMessage.substring(0, 20) + '...' : userMessage || 'New Chat';
    return title;
  }
};

export function useMessagesContexts(enabled: boolean) {
  const { data, isLoading } = useQuery({
    queryKey: ['assistant-messages-contexts'],
    queryFn: async () => {
      const response = await axios.get<MessagesContextResponse>('/mast-alto/assistant/');
      return response.data;
    },
    onError: (error) => notify((error as Error).message, 'error'),
    enabled,
  });

  return { messagesContexts: data, isLoading };
}

export function usePostMessageToAssistant() {
  const [messages, setMessages] = useState<Messages>([]);
  const [streamingContent, setStreamingContent] = useState<string>('');
  const [isStreaming, setIsStreaming] = useState<boolean>(false);
  let streamResponse: Response | null = null;

  const postMessageMutation = useMutation({
    mutationFn: async (payload: { message: string; id?: string }) => {
      setStreamingContent('');

      // Add assistant message placeholder
      setMessages((prev) => [...prev, { role: 'assistant', content: '' }]);

      const response = await axios.post<{ id: string }>('/mast-alto/assistant/', payload);

      const id = response.data.id;

      const baseURL = axios.defaults.baseURL || '';
      streamResponse = await fetch(`${baseURL}mast-alto/assistant/stream/?id=${id}`, {
        headers: {
          Accept: 'text/plain',
          'Content-Type': 'application/json',
          ...((axios.defaults.headers?.common as Record<string, string>) || {}),
        },
      });

      if (!streamResponse.ok) {
        const errorText = await streamResponse.text();
        console.error('Stream response error:', errorText);
        throw new Error(
          `Failed to fetch stream: ${streamResponse.status} ${streamResponse.statusText}`
        );
      }

      if (!streamResponse.body) {
        throw new Error('No stream body available');
      }

      let fullContent = '';

      try {
        setIsStreaming(true);
        for await (const textChunk of parseSSEStream(streamResponse.body)) {
          if (textChunk === '[DONE]') {
            // completion marker - continue
            continue;
          } else if (textChunk === '') {
            // empty chunk represents a newline
            fullContent += '\n';
            setStreamingContent(fullContent);

            // update the last assistant message with streaming content
            setMessages((prevMessages) => {
              const updatedMessages = [...prevMessages];
              const lastMessage = updatedMessages[updatedMessages.length - 1];

              if (lastMessage && lastMessage.role === 'assistant') {
                lastMessage.content = fullContent;
              }

              return updatedMessages;
            });
          } else {
            // directly append the chunk to preserve formatting
            fullContent += textChunk;
            setStreamingContent(fullContent);

            // Update the last assistant message with streaming content
            setMessages((prevMessages) => {
              const updatedMessages = [...prevMessages];
              const lastMessage = updatedMessages[updatedMessages.length - 1];

              if (lastMessage && lastMessage.role === 'assistant') {
                lastMessage.content = fullContent;
              }

              return updatedMessages;
            });
          }
        }
      } finally {
        streamResponse.body?.cancel();
        setIsStreaming(false);
      }

      return { data: fullContent, id };
    },
    onError: (error) => {
      notify((error as Error).message, 'error');
      setIsStreaming(false);
      // remove the failed assistant message
      setMessages((prev) => prev.slice(0, -1));
    },
  });

  return {
    postMessage: postMessageMutation.mutate,
    isPosting: postMessageMutation.isLoading || isStreaming,
    sessionId: postMessageMutation.data?.id,
    messages,
    setMessages,
    streamingContent,
    isStreaming,
  };
}

export function useDeleteContext() {
  const queryClient = useQueryClient();
  const deleteContextMutation = useMutation({
    mutationFn: async (sessionId: string) => {
      const response = await axios.delete(`/mast-alto/assistant/`, { params: { id: sessionId } });
      return response.data;
    },
    onError: (error) => notify((error as Error).message, 'error'),
    onSuccess: () => queryClient.invalidateQueries(['assistant-messages-contexts']),
  });
  return {
    deleteContext: deleteContextMutation.mutate,
    isDeleting: deleteContextMutation.isLoading,
  };
}
